import { useMemo, useState, useEffect } from "react";
import { X } from "lucide-react";
import { GALLERY as TIDS } from "@/constants/testIds";
import { GALLERY_CATEGORIES, GALLERY_ITEMS } from "@/config/site";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

function ImageFrame({ src, alt, className = "", ratio = "4/3", tag }) {
  return (
    <div
      className={`relative overflow-hidden border border-black/10 bg-sand project-photo-frame ${className}`}
      style={{ aspectRatio: ratio }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="project-photo absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03]"
      />
      {tag && (
        <div className="absolute top-3 left-3 bg-anthracite/85 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">
          {tag}
        </div>
      )}
      {/* corner marks */}
      <span className="absolute top-2 left-2 w-3 h-3 border-t border-l border-white/60 pointer-events-none" />
      <span className="absolute top-2 right-2 w-3 h-3 border-t border-r border-white/60 pointer-events-none" />
      <span className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-white/60 pointer-events-none" />
      <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-white/60 pointer-events-none" />
    </div>
  );
}

export default function GalleryPage() {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useSEO({
    title: "Réalisations — Avant / Après en Indre-et-Loire",
    description:
      "Découvrez les chantiers récents de PENOW à Fondettes et en Indre-et-Loire : ravalement de façade, démoussage de toiture, installation de climatisation gainable et split, rénovation de salle de bain. Photos avant / après.",
    keywords:
      "réalisations rénovation Fondettes, avant après façade 37, photos toiture démoussée Indre-et-Loire, chantier climatisation Tours, exemple isolation combles 37",
    path: "/realisations",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "Réalisations PENOW",
      description:
        "Galerie de réalisations : rénovation, climatisation, façade, toiture en Indre-et-Loire",
      image: GALLERY_ITEMS.map((it) => ({
        "@type": "ImageObject",
        name: it.title,
        caption: `${it.title} — ${it.location} (${it.year})`,
        contentUrl: it.image,
      })),
    },
  });

  const items = useMemo(() => {
    if (filter === "all") return GALLERY_ITEMS;
    return GALLERY_ITEMS.filter((i) => i.category === filter);
  }, [filter]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div data-testid={TIDS.section}>
      <section className="container-editorial pt-16 md:pt-24 pb-12">
        <div className="max-w-3xl">
          <div className="eyebrow mb-6">Réalisations</div>
          <h1 className="font-display text-5xl md:text-6xl leading-tight text-anthracite">
            Chaque chantier raconte une histoire.
          </h1>
          <p className="mt-6 text-lg text-anthracite/70 leading-relaxed">
            Une sélection de projets récents réalisés par nos professionnels
            partenaires. Filtrez par type de travaux pour explorer les
            savoir-faire disponibles dans notre réseau.
          </p>
          <p className="mt-3 text-xs text-anthracite/50 italic">
            Chaque réalisation présentée ici a été effectuée par un professionnel
            partenaire de PENOW, sous sa seule responsabilité.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-12 flex flex-wrap gap-2">
          {GALLERY_CATEGORIES.map((c) => {
            const active = filter === c.key;
            const count =
              c.key === "all"
                ? GALLERY_ITEMS.length
                : GALLERY_ITEMS.filter((i) => i.category === c.key).length;
            return (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={c.key === "all" ? TIDS.filterAll : TIDS.filter(c.key)}
                className={`px-4 py-2 text-sm tracking-wide border transition-colors ${
                  active
                    ? "bg-anthracite text-white border-anthracite"
                    : "bg-white text-anthracite border-black/15 hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {c.label}
                <span className={`ml-2 text-[11px] ${active ? "text-white/60" : "text-anthracite/40"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="container-editorial pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <Reveal
              key={it.id}
              delay={(i % 6) * 60}
              data-testid={TIDS.card}
              className="group cursor-pointer"
            >
              <button
                onClick={() => setLightbox(it)}
                className="w-full text-left"
                aria-label={`Voir ${it.title}`}
              >
                <ImageFrame
                  src={it.image}
                  alt={it.title}
                  ratio={it.format === "combo" ? "16/10" : "4/3"}
                  tag={it.format === "combo" ? "Avant · Après" : "Réalisation partenaire"}
                />
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta">
                    {GALLERY_CATEGORIES.find((c) => c.key === it.category)?.label}
                  </div>
                  <div
                    data-testid={TIDS.cardLabel}
                    className="font-display text-xl mt-1 text-anthracite group-hover:text-terracotta transition-colors leading-snug"
                  >
                    {it.title}
                  </div>
                  <div className="text-xs text-anthracite/50 mt-1">
                    {it.location} · {it.year}
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {items.length === 0 && (
          <div className="mt-16 text-center text-anthracite/50">
            Aucune réalisation dans cette catégorie pour le moment.
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] bg-anthracite/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative bg-white max-w-6xl w-full max-h-[92vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 p-2 bg-white/70 hover:bg-black hover:text-white transition-colors z-10"
              aria-label="Fermer"
              data-testid="gallery-lightbox-close"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>

            <div className="p-6 md:p-10">
              <div className="eyebrow mb-2">
                {GALLERY_CATEGORIES.find((c) => c.key === lightbox.category)?.label}
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-anthracite leading-tight">
                {lightbox.title}
              </h3>
              <div className="text-sm text-anthracite/60 mt-2">
                {lightbox.location} · {lightbox.year}
              </div>

              <div className="mt-6">
                <ImageFrame
                  src={lightbox.image}
                  alt={lightbox.title}
                  ratio={lightbox.format === "combo" ? "16/10" : "16/10"}
                  tag={lightbox.format === "combo" ? "Avant · Après" : null}
                />
              </div>

              {lightbox.description && (
                <p className="mt-6 text-anthracite/70 leading-relaxed max-w-3xl">
                  {lightbox.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
